<script>
import { defineComponent } from 'vue'
import exponentialBackoffDelayMixin from '../../../mixins/exponentialBackoffDelayMixin'
import { delayFrames } from '../../../utility'

const thumbnailTransitionBackgroundDuration = 150

export default defineComponent({
    name: 'advanced-thumbnail-impl',
    mixins: [exponentialBackoffDelayMixin],
    props: {
        alt: String,
        thumb: String,
        backgroundType: String,
        backgroundPosition: String,
        backgroundSize: String,
        shadow: {
            type: Boolean,
            default: true
        },
        minWidthForStateAndIcon: Boolean
    },
    data() {
        return {
            fallback: false,
            imgCompleted: false,
            fallbackCompleted: false,
            fallbackCompletedAfterTick: false,
            imgSrcCached: false,

            showShimmer: !!this.thumb,

            imgReloadTimeout: null,
            fallbackTimeout: null,

            // data used if 'applyBgSizingAndPositioning' computes to true
            backgroundImage: null,
            thumbOverride: false // Whether to override img src thumb (and set to null) to display background image instead
        }
    },
    mounted() {
        this.checkImgCached()
    },
    beforeDestroy() {
        clearTimeout(this.imgReloadTimeout)
        clearTimeout(this.fallbackTimeout)
    },
    computed: {
        containerStyle() {
            return {
                'background-color': (this.imgCompleted || this.fallbackCompleted) ? undefined : '#eeeeee',
                '--thumbnail-transition-background-duration': `${thumbnailTransitionBackgroundDuration}ms`
            }
        },
        imgStyle() {
            const style = {
                opacity: this.imgCompleted ? 1 : 0
            }

            if (this.thumbOverride) {
                style.backgroundImage = this.backgroundImage
                style.backgroundSize = this.backgroundSize
                style.backgroundPosition = this.backgroundPosition
            }

            return style
        },
        fallbackImgStyle() {
            return {
                opacity: this.fallbackCompletedAfterTick ? 1 : 0
            }
        },
        bgStyle() {
            return {
                opacity: this.imgCompleted ? 1 : 0
            }
        },
        bgCheckered() {
            return this.backgroundType == 'checkered'
        },
        bgSolid() {
            return this.backgroundType == 'solid'
        },
        applyBgSizingAndPositioning() {
            return this.backgroundSize
        },
        useExtraDivForBg() {
            return true
            /* TODO - should be true when shown in publish tables previews, false otherwise
                (better performance with no extra div if used in templatecreator previews. Just add a new prop? */
        }
    },
    methods: {
        checkImgCached() {
            const img = this.$refs.imgRef

            if (img?.complete) {
                if (img?.naturalWidth > 0) {
                    this.imgSrcCached = true
                    this.onImgLoad(img)
                } else {
                    this.onImgError()
                }
            }
        },
        onImgLoad() {
            if (this.applyBgSizingAndPositioning) {
                this.backgroundImage = `url("${this.thumb}")`
                this.thumbOverride = true
            }

            this.imgCompleted = true
            this.fallbackCompleted = false

            this.fallbackTimeout = setTimeout(() => {
                this.fallback = false
                this.showShimmer = false
            }, thumbnailTransitionBackgroundDuration)
        },
        onImgError() {
            this.fallback = true
            const timeout = this.getDelayValue();
            if (timeout) {
                this.imgReloadTimeout = setTimeout(() => this.$refs.imgRef.src = this.thumb, timeout)
            }
        },
        onFallbackLoad() {
            this.fallbackCompleted = true
            this.fallbackTimeout = setTimeout(() => {
                this.showShimmer = false
            }, thumbnailTransitionBackgroundDuration)
        },
        onFallbackError() {
            this.fallbackCompleted = true
        }
    },
    watch: {
        async fallbackCompleted(val) {
            if (val) {
                await delayFrames(2)
                this.fallbackCompletedAfterTick = true
            } else {
                this.fallbackCompletedAfterTick = false
            }
        },
        async thumb(val) {
            /* Restart image loading from src when thumb changes (when component updates and only re-renders) */
            this.backgroundImage = null
            this.thumbOverride = false
            this.fallback = false
            this.imgCompleted = false
            this.fallbackCompleted = false
            this.fallbackCompletedAfterTick = false
            clearTimeout(this.imgReloadTimeout)
            clearTimeout(this.fallbackTimeout)
            this.showShimmer = !!val
            this.imgSrcCached = false

            // Wait for thumb value to be pushed to DOM
            await this.$nextTick()
            this.checkImgCached()
        }
    }
})
</script>

<template>
    <div :class=" {
            'shimmer-div': showShimmer,
            'not-visible': (fallbackCompleted || imgCompleted) && showShimmer,
            'elevation-1': shadow,
            'min-width-for-state-and-icon': minWidthForStateAndIcon
         }"
         :style="containerStyle"
         class="advanced-thumbnail-container">

        <!--suppress HtmlUnknownTarget -->
        <img v-if="fallback"
             :alt="alt"
             :style="fallbackImgStyle"
             class="advanced-thumbnail-img advanced-thumbnail-fill-and-transparent"
             src="/storage/thumb/fallback"
             @error="onFallbackError"
             @load="onFallbackLoad"
        />

        <div v-if="useExtraDivForBg"
             :class="{
                'bg-checkered': bgCheckered,
             }"
             :style="bgStyle"
             class="advanced-thumbnail-fill-and-transparent advanced-thumbnail-img-background" />

        <img v-if="thumb"
             ref="imgRef"
             :alt="alt"
             :class="{
                'bg-checkered': !useExtraDivForBg && bgCheckered,
                'opacity-transition-enabled': !imgSrcCached
             }"
             :src="thumbOverride ? 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=' : thumb"
             :style="imgStyle"
             class="advanced-thumbnail-img advanced-thumbnail-fill-and-transparent"
             @error="onImgError"
             @load="onImgLoad"
        />

        <slot v-else
              @shimmer-show="showShimmer = true"
              @shimmer-hide="showShimmer = false" />
    </div>
</template>
